import { useState } from 'react';
import { ChevronDown, Mail } from 'lucide-react';

import { Collapse } from '@/components/Collapse';
import { cn } from '@/lib/cn';
import type { Braindump } from './types';

/**
 * Collapsed email source for a captured message.
 * Tasks are the point of the queue; this is reference material on request.
 */
export function EmailSource({ dump }: { dump: Braindump }) {
  const [open, setOpen] = useState(false);

  const subject = dump.email_subject_display || dump.email_subject || '(no subject)';
  const thread = dump.email_thread?.length
    ? dump.email_thread
    : dump.raw_text
      ? [
          {
            from_addr: dump.email_from,
            to_addr: null,
            subject,
            sent: null,
            body: dump.raw_text,
          },
        ]
      : [];

  if (thread.length === 0) return null;

  return (
    <div className="border-t border-border px-3 py-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full min-h-touch items-center gap-2 text-left text-small text-text-secondary hover:text-text"
      >
        <Mail size={14} strokeWidth={1.5} className="shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate">
          {open ? 'Hide email' : 'View email'}
          {!open && subject !== '(no subject)' && (
            <span className="text-text-tertiary"> · {subject}</span>
          )}
        </span>
        <ChevronDown
          size={14}
          strokeWidth={1.5}
          aria-hidden
          className={cn('shrink-0 transition-transform duration-press', open && 'rotate-180')}
        />
      </button>

      <Collapse open={open}>
        <div className="mt-2 flex flex-col gap-3">
          {thread.map((part, index) => (
            <article
              key={index}
              className={cn(
                'rounded border border-border bg-surface-2 px-3 py-2',
                index > 0 && 'opacity-90',
              )}
            >
              <dl className="grid grid-cols-1 gap-0.5 text-label text-text-tertiary">
                {part.from_addr && (
                  <div className="flex gap-2">
                    <dt className="w-10 shrink-0">From</dt>
                    <dd className="min-w-0 truncate text-text-secondary">{part.from_addr}</dd>
                  </div>
                )}
                {part.to_addr && (
                  <div className="flex gap-2">
                    <dt className="w-10 shrink-0">To</dt>
                    <dd className="min-w-0 truncate text-text-secondary">{part.to_addr}</dd>
                  </div>
                )}
                {part.subject && (
                  <div className="flex gap-2">
                    <dt className="w-10 shrink-0">Subj</dt>
                    <dd className="min-w-0 truncate text-text-secondary">{part.subject}</dd>
                  </div>
                )}
                {part.sent && (
                  <div className="flex gap-2">
                    <dt className="w-10 shrink-0">Sent</dt>
                    <dd className="min-w-0 truncate text-text-secondary">{part.sent}</dd>
                  </div>
                )}
              </dl>
              <p className="mt-2 whitespace-pre-wrap break-words text-small text-text-secondary">
                {part.body}
              </p>
            </article>
          ))}
        </div>
      </Collapse>

      {dump.email_deleted_at && (
        <p className="mt-2 text-label text-text-tertiary">
          Removed from the mail server after tasks were approved.
        </p>
      )}
    </div>
  );
}
